import { BaseApiService } from "@mirage/services/BaseApiService";
import { PreApplicationExternalInsertRecordResult } from "@shared/mirage/contracts/PrePublicPortal";
import { IInformationMessage } from "@shared/mirage/core/contracts/IErrorMessage";
import { FileChunk } from "@shared/mirage/core/contracts/UploadedFile";
import { PrePublicPortalWlConstants } from "@shared/prePublicPortalWestLindsay/constants";
import { IPrePpWlForm, IPrePpWlFormField } from "@shared/prePublicPortalWestLindsay/contracts/IPrePpWlForm";
import { MirageConstants } from "@shared/mirage/mirageConstants";
import { StringsHelper } from "@shared/mirage/helpers/StringsHelper";

export class PrePublicPortalWlFormService extends BaseApiService {
    constructor() {
        super(`${PrePublicPortalWlConstants.AppName}/form/`);
    }

    async submitForm(req: IPrePpWlForm, relUrl: string): Promise<PreApplicationExternalInsertRecordResult> {
        const uuid = StringsHelper.generateUUID();
        const chunkSize = MirageConstants.MaxChunkSize;
        const fileMap = new Map<string, number>();
        const siteLocationDocumentsUploaded = await this.uploadAttachments(fileMap, req.siteLocationDocuments, chunkSize, uuid);
        const optionalUploaded = await this.uploadAttachments(fileMap, req.optionalDocuments, chunkSize, uuid);
        if (!siteLocationDocumentsUploaded || !optionalUploaded) {
            return { errorMsg: "An error occurred while submitting the form" };
        }
        const response = await this.httpInstance().post("/submitForm/", { ...req, uuid, relUrl });
        return response.data;
    }
    private async uploadAttachments(fileMap: Map<string, number>, field: IPrePpWlFormField<File[]>, chunkSize: number, uuid: string) {
        const files = field.value;
        const requests = [];
        for (const file of files) {
            if (!fileMap.has(file.name)) {
                fileMap.set(file.name, 1);
            } else {
                const cnt = fileMap.get(file.name) as number;
                fileMap.set(file.name, cnt + 1);
            }
            const fileName = `${fileMap.get(file.name)}.${file.name}`;
            const totalChunks = Math.ceil(file.size / chunkSize);
            let startByte = 0;
            for (let i = 1; i <= totalChunks; i++) {
                const endByte = Math.min(startByte + chunkSize, file.size);
                const chunk = file.slice(startByte, endByte, "application/octet-stream");
                const promise = this.uploadChunk(fileName, chunk, i, totalChunks, uuid);
                startByte = endByte;
                requests.push(promise);
            }
        }
        const responses = await Promise.all(requests);
        field.value = files.map((file) => {
            const fileName = `${fileMap.get(file.name)}.${file.name}`;
            return { name: fileName } as File;
        });
        return responses.every((r) => r);
    }

    private async uploadChunk(fileName: string, chunk: Blob, chunkNumber: number, totalChunks: number, uuid: string) {
        try {
            const payload: FileChunk = {
                fileName,
                chunk: Object.values(new Uint8Array(await chunk.arrayBuffer())),
                uuid,
                chunkNumber,
                totalChunks,
            };

            const response = await this.httpInstance().post("/uploadChunk/", payload);
            return response.status === 200;
        } catch (error) {
            console.error("Error uploading chunk", error);
            return false;
        }
    }

    async checkPayment(payment: string): Promise<IInformationMessage> {
        const response = await this.httpInstance().get(`/checkPayment/${payment}`);
        return response.data;
    }
}
